import type {
  GetMbcBriefResponse,
  MbcBrief,
  MbcBriefQuality,
} from "@nlc/shared-types";

import { useCallback, useEffect, useState } from "react";

import { MBC_MASTRA_API_URL } from "../config/mbc-chat.config";

export type BriefMemory = MbcBrief;

type UseBriefMemoryArgs = {
  /** Bump this after each completed turn to refetch the persisted form. */
  refreshSignal: number;
  resourceId: string;
  threadId: string;
};

function briefUrl(threadId: string, resourceId: string): string {
  const params = new URLSearchParams({ resourceId });

  return `${MBC_MASTRA_API_URL}/mbc/brief/${encodeURIComponent(threadId)}?${params.toString()}`;
}

/**
 * Reads the agent's working memory (the brief form Mastra persists in MongoDB)
 * through the AI module's `/mbc/brief/:threadId` route, which reads the same
 * underlying document as Mastra's generic memory API but also returns
 * the deterministic quality card (no JSON string to parse — already an object).
 *
 * Uses a plain fetch (not @mastra/client-js) to keep the browser bundle light
 * and avoid that SDK's Node-only transitive deps.
 */
export function useBriefMemory({
  refreshSignal,
  resourceId,
  threadId,
}: UseBriefMemoryArgs): {
  error: unknown;
  memory: BriefMemory | null;
  quality: MbcBriefQuality | null;
  refetch: () => Promise<void>;
} {
  const [memory, setMemory] = useState<BriefMemory | null>(null);
  const [quality, setQuality] = useState<MbcBriefQuality | null>(null);
  const [error, setError] = useState<unknown>(null);

  const fetchMemory = useCallback(async () => {
    try {
      const response = await fetch(briefUrl(threadId, resourceId));

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const body = (await response.json()) as GetMbcBriefResponse;
      const brief = body.brief ?? null;

      setMemory(brief);
      setQuality(body.quality ?? null);
      setError(null);
    } catch (error_) {
      setError(error_);
    }
  }, [resourceId, threadId]);

  useEffect(() => {
    // Fetch right away, then once more shortly after — the agent persists
    // working memory near the end of the stream, so a single fetch on
    // turn-complete can occasionally race ahead of the Mongo write.
    void fetchMemory();
    const timer = setTimeout(() => void fetchMemory(), 900);

    return () => clearTimeout(timer);
  }, [fetchMemory, refreshSignal]);

  return { error, memory, quality, refetch: fetchMemory };
}
